import { Link } from "react-router-dom";
import { ArrowRight, Leaf, RadioTower, Cpu, Scale, Users, Telescope } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import CTASection from "@/components/shared/CTASection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FOCUS_AREAS } from "@/data/content";
import { FOCUS } from "@/constants/testIds";

const ICONS = {
  leaf: Leaf,
  "radio-tower": RadioTower,
  cpu: Cpu,
  scale: Scale,
  users: Users,
  telescope: Telescope,
};

export default function FocusAreas() {
  return (
    <>
      <PageHeader
        overline="Focus Areas"
        title="Six pillars that anchor the Centre's work."
        lead="From the convergence of climate adaptation, disaster risk reduction, and responsible technology, the Centre organises its work around six thematic pillars. Each is summarised here, with a path to deeper material."
      />

      <section className="ccdr-section">
        <div className="ccdr-container">
          <Accordion type="multiple" className="space-y-3">
            {FOCUS_AREAS.map((f, idx) => {
              const Icon = ICONS[f.icon] || Leaf;
              return (
                <AccordionItem
                  key={f.slug}
                  value={f.slug}
                  id={f.slug}
                  data-testid={FOCUS.accordion(f.slug)}
                  className="border border-[#E5E7EB] bg-white px-6"
                >
                  <AccordionTrigger className="hover:no-underline py-6">
                    <div className="flex items-start gap-6 text-left w-full pr-6">
                      <span
                        className="text-2xl tabular-nums"
                        style={{ color: "var(--ccdr-accent-dark)", fontWeight: 300 }}
                      >
                        0{idx + 1}
                      </span>
                      <Icon className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: "var(--ccdr-primary)" }} />
                      <div className="flex-1">
                        <h3
                          className="text-xl lg:text-2xl"
                          style={{ fontWeight: 500, color: "var(--ccdr-primary-dark)" }}
                        >
                          {f.title}
                        </h3>
                        <p className="text-[#4B5563] mt-2 font-normal">
                          {f.short}
                        </p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8">
                    <div className="pl-16 pr-6 ccdr-prose max-w-3xl">
                      <p>{f.long}</p>
                      {f.deepLink && (
                        <Link
                          to={f.deepLink}
                          className="ccdr-btn-outline mt-6"
                          data-testid={FOCUS.card(f.slug)}
                        >
                          Open the dedicated tab <ArrowRight className="w-4 h-4" />
                        </Link>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      <CTASection
        title="Choose a focus area to go deeper, or browse the latest insights."
        primary={{ to: "/insights", label: "Read insights" }}
        secondary={{ to: "/frameworks", label: "Or open the frameworks library" }}
      />
    </>
  );
}
